import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import TableContainer from "@mui/material/TableContainer";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services
import CrudService from "services/cruds-service";
import { Paper } from "@mui/material";
import { apiRequest } from "utils/apiUtils";
import { useWebSocket } from "context/WebSocketContext";

// const RouteMapLeaflet = lazy(() => import("./RouteMapLeaflet"));
const JobRouteMapGoogle = lazy(() => import("./JobRouteMapGoogle"));

const STATE_HEX = {
  IDLE: "#6B7280",
  SETUP: "#F59E0B",
  PUMPING: "#3B82F6",
  PRIMING: "#F97316",
  COLLECTING: "#14B8A6",
  LOADED: "#84CC16",
  HOLD: "#A855F7",
  FINISHING: "#F97316",
  DISCHARGE: "#EF4444",
  DISCHARGING: "#EC4899",
  COMPLETE: "#10B981",
  COMPLETED: "#10B981",
  ERROR: "#DC2626",
};

const TRANSITION_COLUMN_DEFAULTS = {
  id: true,
  start: true,
  stop: true,
  stageStart: true,
  stageEnd: true,
  delta: true,
  reason: true,
  location: true,
  duration: true,
  confidence: true,
  time: true,
  timeAgo: true,
};

const TRANSITIONS_COLUMN_STORAGE_KEY =
  "rivio-job-view-transitions-visible-columns";
const LOCATION_BUCKET_PRECISION = 3;

export default function JobViewPage() {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  // --- State Management ---
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAnswer, setAiAnswer] = useState("");
  const [showRouteMap, setShowRouteMap] = useState(true);
  const [fullscreenMap, setFullscreenMap] = useState(false);
  const [routeTrackPoints, setRouteTrackPoints] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeSummary, setRouteSummary] = useState(null);
  const [jobStages, setJobStages] = useState([]);
  const [stagesByTransitionId, setStagesByTransitionId] = useState(new Map());
  const [showTransitionsSection, setShowTransitionsSection] = useState(true);
  const [visibleColumns, setVisibleColumns] = useState(
    TRANSITION_COLUMN_DEFAULTS,
  );
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [transitionSearch, setTransitionSearch] = useState("");
  const [showDeltaOnly, setShowDeltaOnly] = useState(false);
  const [showTransitionTypeTotals, setShowTransitionTypeTotals] =
    useState(true);
  const [showLocationTotals, setShowLocationTotals] = useState(true);
  const [isPreviewingPDF, setIsPreviewingPDF] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [expandedTransitionIds, setExpandedTransitionIds] = useState(new Set());
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showValidOnly, setShowValidOnly] = useState(true);
  const { jobState } = useWebSocket();


  useEffect(() => {
    if (jobState && job && job?.device_id == jobState.device_id) {
      fetchJobWithoutRefresh()
    }
  }, [jobState])

  const normalizeJobPayload = (raw) => {
    if (!raw) return null;
    const payload = raw.data ?? raw;
    if (payload?.id && Array.isArray(payload?.transition_logs)) return payload;
    if (payload?.job && typeof payload.job === "object") {
      return {
        ...payload.job,
        transition_logs: Array.isArray(payload.transitions)
          ? payload.transitions
          : Array.isArray(payload.transition_logs)
            ? payload.transition_logs
            : [],
      };
    }
    return payload?.id
      ? {
        ...payload,
        transition_logs: Array.isArray(payload.transition_logs)
          ? payload.transition_logs
          : Array.isArray(payload.transitions)
            ? payload.transitions
            : [],
      }
      : null;
  };

  const fetchJob = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CrudService.getJobDetail(jobId);
      if (res) {
        setJob(normalizeJobPayload(res));
      } else {
        setError(res?.error || "Failed to fetch job");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchJobWithoutRefresh = useCallback(async () => {
    try {
      const res = await CrudService.getJobDetail(jobId);
      if (res) {
        setJob(normalizeJobPayload(res));
      } else {
        setError(res?.error || "Failed to fetch job");
      }
    } catch (err) {
      setError(err.message);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchJob()
    };
  }, [jobId, fetchJob]);

  // --- Populate Stages Map ---
  useEffect(() => {
    if (!job?.id) {
      setJobStages([]);
      setStagesByTransitionId(new Map());
      return;
    }
    CrudService.getJobStages(jobId).then((res) => {
      const stages = res?.stages ?? [];
      setJobStages(stages);
      const map = new Map();
      for (const s of stages) {
        if (s.start_transition_log_id != null) {
          const existing = map.get(s.start_transition_log_id);
          if (!existing || existing.role !== "end") {
            map.set(s.start_transition_log_id, { stage: s, role: "start" });
          }
        }
        if (s.end_transition_log_id != null) {
          map.set(s.end_transition_log_id, { stage: s, role: "end" });
        }
      }
      setStagesByTransitionId(map);
    });
  }, [job]);

  useEffect(() => {
    if (!job?.id) {
      return;
    }

    const fetchRoutes = async () => {
      setRouteLoading(true);
      try {
        CrudService.getJobGpsWindow(jobId).then((data) => {
          if (data.success) {
            const pts = data.points
              .map((p) => ({
                lat: Number(p.latitude),
                lng: Number(p.longitude),
                timestamp: p.timestamp,
                speed: p.speed != null ? Number(p.speed) : undefined,
                heading: p.heading,
                status: p.status,
              }))
              .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
            setRouteTrackPoints(pts);
            // Derive duration from the job window
            if (data.window?.start_time && data.window?.end_time) {
              const durationMin =
                (new Date(data.window.end_time).getTime() -
                  new Date(data.window.start_time).getTime()) /
                1000 /
                60;
              setRouteSummary({
                totalDistanceMi: 0,
                totalDurationMin: durationMin,
              });
            }
          }
        });
      } catch (error) {
        console.warn("[JobViewPage] Could not load GPS window data:", err);
      } finally {
        setRouteLoading(false);
      }
    };
    fetchRoutes();
  }, [job]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(TRANSITIONS_COLUMN_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      setVisibleColumns({ ...TRANSITION_COLUMN_DEFAULTS, ...parsed });
    } catch {
      // ignore errors
    }
  }, []);

  const handleAnalyzeWithAi = async () => {
    if (!job) return;

    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiAnswer("");

    const transitions = Array.isArray(job.transition_logs)
      ? job.transition_logs
      : [];
    const failures = transitions
      .filter((t) => t?.validation_context?.validation?.failures?.length)
      .slice(-20)
      .map((t) => ({
        id: t.id,
        from_state: t.from_state,
        to_state: t.to_state,
        timestamp: t.transition_timestamp,
        trigger_reason: t.trigger_reason,
        failures: t.validation_context?.validation?.failures || [],
        debug: t.validation_context?.validation?.debug || [],
      }));

    const latest = transitions.slice(-10).map((t) => ({
      id: t.id,
      from_state: t.from_state,
      to_state: t.to_state,
      timestamp: t.transition_timestamp,
      trigger_reason: t.trigger_reason,
      allowed: t.validation_context?.validation?.allowed ?? null,
      confidence: t.validation_context?.validation?.confidence ?? null,
    }));

    const payload = {
      job: {
        id: job.id,
        job_session_name: job.job_session_name,
        current_state: job.current_state,
        operation_config_id: job.operation_config_id,
        device_id: job.device_id,
        entity_address_item_id: job.entity_address_item_id,
        started_at: job.started_at,
        completed_at: job.completed_at,
        updated_at: job.updated_at,
        metadata: job.metadata,
      },
      stats: {
        total_transitions: transitions.length,
        valid_transitions: transitions.filter(
          (t) => t.validation_context?.validation?.allowed === true,
        ).length,
        invalid_transitions: transitions.filter(
          (t) => t.validation_context?.validation?.allowed === false,
        ).length,
      },
      transitions: {
        latest,
        failures,
      },
    };
    // return

    const userPrompt = `Analyze this channel-job payload and explain why the transition did not occur. Focus on validation failures, threshold rules, and expected states. Provide a concise diagnosis and 3-5 actionable next checks. Explicitly call out which transition(s) or rule(s) should be changed (and how) to achieve the desired transition.\n\nPayload:\n${JSON.stringify(
      payload,
      null,
      2,
    )}`;

    try {
      const response = await apiRequest("/api/ai/app-chat", {
        method: "POST",
        body: JSON.stringify({
          messages: [{ role: "user", content: userPrompt }],
          model: "gpt-5-mini",
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.error || `AI request failed (${response.status})`,
        );
      }

      const answerText = typeof data?.answer === "string" ? data.answer : "";
      setAiAnswer(answerText || "No answer returned.");
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "AI request failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!job) return;

    const reportPayload = buildReportPayload();
    if (!reportPayload) return;

    setIsPreviewingPDF(true);
    try {
      // The server now returns HTML, so just open the response in a new tab
      const response = await CrudService.previewJob(reportPayload);
      // const html = await response.text();

      const previewWindow = window.open('', '_blank');
      if (!previewWindow) {
        throw new Error('Popup blocked');
      }

      // Write HTML content directly
      previewWindow.document.write(response);
      previewWindow.document.close();
    } catch (error) {
      console.error('[JobViewPage] Error previewing report:', error);
      alert('Failed to preview report: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsPreviewingPDF(false);
    }
  };

  const handleExportPdf = async () => {
    if (!job) return;

    const reportPayload = buildReportPayload();
    if (!reportPayload) return;

    setIsExportingPDF(true);
    try {
      const response = await CrudService.downloadJobPdf(reportPayload)

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = `JobSession_${job.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[JobViewPage] Error exporting PDF:', error);
      alert('Failed to export PDF: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExportingPDF(false);
    }
  };

  const exportTransitionsToCSV = async () => {
    if (!job) return;

    const transitions = getSortedTransitions();
    const transitionRows = transitions.map((transition) => {
      const validation = transition.validation_context?.validation;
      return {
        id: transition.id,
        from_state: transition.from_state,
        to_state: transition.to_state,
        trigger_reason: transition.trigger_reason,
        trigger_channel: transition.trigger_channel,
        trigger_value: transition.trigger_value,
        transition_timestamp: transition.transition_timestamp,
        allowed: validation?.allowed ?? null,
        failures_count: validation?.failures?.length ?? 0,
        failures: validation?.failures ? JSON.stringify(validation.failures) : null,
        confidence: validation?.confidence ?? null,
        validation_debug: validation?.debug ? JSON.stringify(validation.debug) : null,
        validation_context: transition.validation_context ? JSON.stringify(transition.validation_context) : null
      };
    });

    const jobSummary = [
      {
        job_id: job.id,
        job_session_name: job.job_session_name,
        current_state: job.current_state,
        operation_config_id: job.operation_config_id,
        entity_address_item_id: job.entity_address_item_id,
        device_id: job.device_id,
        started_at: job.started_at,
        state_changed_at: job.state_changed_at || job.updated_at,
        completed_at: job.completed_at,
        total_duration_seconds: job.total_duration_seconds,
        volume_collected_gallons: job.volume_collected_gallons,
        volume_discharged_gallons: job.volume_discharged_gallons,
        residual_volume_gallons: job.residual_volume_gallons,
        gps_track_points: routeTrackPoints.length || null,
        gps_total_distance_mi: routeSummary ? Number(routeSummary.totalDistanceMi.toFixed(2)) : null,
        gps_total_duration_min: routeSummary ? Math.round(routeSummary.totalDurationMin) : null,
        metadata: job.metadata ? JSON.stringify(job.metadata) : null,
        exported_at: new Date().toISOString(),
        filter_valid_only: showValidOnly
      }
    ];

    const response = await CrudService.downloadJobExcel({ jobSummary, transitionRows, jobId: job.id })

    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.download = `job-${job.id}-transitions.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getReportTransitions = () => {
    const transitions = Array.isArray(job?.transition_logs) ? job.transition_logs : [];
    if (!showValidOnly) return transitions;

    return transitions.filter((transition) => {
      const isValid = transition.validation_context?.validation?.allowed === true;
      const triggerReason = String(transition?.trigger_reason || '').toUpperCase();
      const isAdminForceComplete = triggerReason === 'ADMIN_FORCE_COMPLETE';
      return isValid || isAdminForceComplete;
    });
  };

  const getLocationGallonTotalsForTransitions = (transitions) => {
    const byLocation = new Map();

    (transitions || []).forEach((transition) => {
      const ref = stagesByTransitionId.get(transition.id);
      if (!ref) return;

      const delta = Number(ref.stage.delta);
      if (!Number.isFinite(delta)) return;

      const snapshot = transition.linked_device_snapshot || transition.validation_context?.linked_device_snapshot;
      const children = Array.isArray(snapshot?.children) ? snapshot.children : [];
      const coordSource = children.find((child) => Number.isFinite(Number(child?.lat)) && Number.isFinite(Number(child?.lng)));

      const lat = coordSource ? Number(coordSource.lat) : null;
      const lng = coordSource ? Number(coordSource.lng) : null;
      const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

      const locationLabel =
        transition?.location?.formatted_address
        || coordSource?.reverse_geocoded_address?.formatted_address
        || (coordSource?.location && String(coordSource.location) !== 'N/A' ? String(coordSource.location) : null)
        || `${coordSource.position_latitude} | ${coordSource.position_longitude}`;

      const coords = hasCoords ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '—';
      const locationKey = hasCoords
        ? `${lat.toFixed(LOCATION_BUCKET_PRECISION)}|${lng.toFixed(LOCATION_BUCKET_PRECISION)}`
        : `unknown|${locationLabel}`;

      const existing = byLocation.get(locationKey) ?? {
        label: locationLabel,
        coords,
        count: 0,
        net: 0,
        abs: 0,
      };
      if (existing.label === 'Unknown Location' && locationLabel !== 'Unknown Location') {
        existing.label = locationLabel;
      }
      existing.count += 1;
      existing.net += delta;
      byLocation.set(locationKey, existing);
    });

    const rows = Array.from(byLocation.values())
      .map((row) => ({
        ...row,
        abs: Math.abs(row.net),
      }))
      .sort((a, b) => b.abs - a.abs);
    return {
      rows,
      totalNet: rows.reduce((acc, row) => acc + row.net, 0),
    };
  };

  const buildReportPayload = () => {
    if (!job) return null;

    const reportTransitions = getReportTransitions();
    const locationGallonTotals = getLocationGallonTotalsForTransitions(reportTransitions);

    return {
      job,
      transitions: reportTransitions,
      stages: jobStages,
      report_options: {
        valid_only: showValidOnly,
        transition_count: reportTransitions.length,
        include_map: false,
        gps_track_points: routeTrackPoints.length || null,
        gps_total_distance_mi: routeSummary ? Number(routeSummary.totalDistanceMi.toFixed(2)) : null,
        gps_total_duration_min: routeSummary ? Math.round(routeSummary.totalDurationMin) : null,
        gallon_location_totals: locationGallonTotals.rows,
        gallon_location_total_net: Number(locationGallonTotals.totalNet.toFixed(2)),
      }
    };
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}h ${m}m ${s}s`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "Invalid Date";
      return dateObj.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (err) {
      console.error("[JobViewPage] Error formatting date:", date, err);
      return "Invalid Date";
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return "—";
    const timestamp = new Date(date).getTime();
    if (!Number.isFinite(timestamp)) return "—";

    const diffSeconds = Math.max(
      0,
      Math.floor((Date.now() - timestamp) / 1000),
    );
    if (diffSeconds < 60) return `${diffSeconds}s ago`;

    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;

    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return `${diffMonths}mo ago`;

    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}y ago`;
  };

  const formatVolumeDisplay = (value) => {
    if (value === null) return "—";
    return `${value.toFixed(1)} gal`;
  };

  const getVolumeValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const toggleTransitionExpanded = (id) => {
    const newExpanded = new Set(expandedTransitionIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTransitionIds(newExpanded);
  };

  const toggleAllTransitionsExpanded = () => {
    const visibleTransitionIds = filteredTransitions.map(
      (transition) => transition.id,
    );
    const allVisibleExpanded =
      visibleTransitionIds.length > 0 &&
      visibleTransitionIds.every((id) => expandedTransitionIds.has(id));

    if (allVisibleExpanded) {
      setExpandedTransitionIds(new Set());
      return;
    }

    setExpandedTransitionIds(new Set(visibleTransitionIds));
  };

  const toggleVisibleColumn = (column) => {
    setVisibleColumns((prev) => {
      const currentlyVisible = Object.values(prev).filter(Boolean).length;
      if (prev[column] && currentlyVisible <= 1) return prev;
      return { ...prev, [column]: !prev[column] };
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const resolveTransitionLocationLabel = (transition) => {
    if(transition?.location){
      return transition?.location?.formatted_address
    }
    const snapshot =
      transition.linked_device_snapshot ||
      transition.validation_context?.linked_device_snapshot;
    const children = Array.isArray(snapshot?.children) ? snapshot.children : [];
    const coordSource = children.find(
      (child) =>
        Number.isFinite(Number(child?.lat)) &&
        Number.isFinite(Number(child?.lng)),
    );

    return (
      coordSource?.reverse_geocoded_address?.formatted_address ||
      (coordSource?.location && String(coordSource.location) !== "N/A"
        ? String(coordSource.location)
        : null) ||
      `${coordSource.position_latitude} | ${coordSource.position_longitude}`
    );
  };

  const getSortedTransitions = () => {
    if (!job?.transition_logs) return [];

    const baseTransitions = showValidOnly
      ? job.transition_logs.filter(
        (transition) =>
          transition.validation_context?.validation?.allowed === true,
      )
      : job.transition_logs;

    const durationById = getDurationById(baseTransitions);

    const getLocationLabel = (transition) => {
      if(transition?.location){
        return transition?.location?.formatted_address
      }
      const snapshot =
        transition.linked_device_snapshot ||
        transition.validation_context?.linked_device_snapshot;
      const children = Array.isArray(snapshot?.children)
        ? snapshot.children
        : [];
      const coordSource = children.find(
        (child) =>
          Number.isFinite(Number(child?.lat)) &&
          Number.isFinite(Number(child?.lng)),
      );
      return (
        coordSource?.reverse_geocoded_address?.formatted_address ||
        (coordSource?.location && String(coordSource.location) !== "N/A"
          ? String(coordSource.location)
          : null) ||
        `${coordSource.position_latitude} | ${coordSource.position_longitude}`
      );
    };

    const sorted = [...baseTransitions].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === "id") {
        aVal = a.id;
        bVal = b.id;
      } else if (sortColumn === "start") {
        aVal = a.from_state ?? "";
        bVal = b.from_state ?? "";
      } else if (sortColumn === "stop") {
        aVal = a.to_state ?? "";
        bVal = b.to_state ?? "";
      } else if (sortColumn === "reason") {
        aVal = a.trigger_reason ?? "";
        bVal = b.trigger_reason ?? "";
      } else if (sortColumn === "location") {
        aVal = getLocationLabel(a);
        bVal = getLocationLabel(b);
      } else if (sortColumn === "duration") {
        aVal = durationById.get(a.id) ?? -1;
        bVal = durationById.get(b.id) ?? -1;
      } else if (sortColumn === "confidence") {
        aVal = a.validation_context?.validation?.confidence ?? -1;
        bVal = b.validation_context?.validation?.confidence ?? -1;
      } else if (sortColumn === "timestamp" || sortColumn === "timeAgo") {
        aVal = new Date(a.transition_timestamp).getTime();
        bVal = new Date(b.transition_timestamp).getTime();
      } else if (sortColumn === "delta") {
        const aStage = stagesByTransitionId.get(a.id);
        const bStage = stagesByTransitionId.get(b.id);
        const aDelta = aStage ? Number(aStage.stage.delta) : null;
        const bDelta = bStage ? Number(bStage.stage.delta) : null;
        aVal = aDelta != null && Number.isFinite(aDelta) ? aDelta : -Infinity;
        bVal = bDelta != null && Number.isFinite(bDelta) ? bDelta : -Infinity;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getDurationById = (transitions) => {
    const durationMap = new Map();
    if (!transitions || transitions.length === 0) return durationMap;

    const chronological = [...transitions].sort(
      (a, b) =>
        new Date(a.transition_timestamp).getTime() -
        new Date(b.transition_timestamp).getTime(),
    );

    for (let i = 0; i < chronological.length; i += 1) {
      const current = chronological[i];
      const next = chronological[i + 1];
      const currentTime = new Date(current.transition_timestamp).getTime();
      const nextTime = next
        ? new Date(next.transition_timestamp).getTime()
        : new Date().getTime();
      const diffSeconds = Math.max(
        0,
        Math.floor((nextTime - currentTime) / 1000),
      );
      durationMap.set(current.id, diffSeconds);
    }

    return durationMap;
  };

  const getFilteredTransitions = () => {
    const transitions = getSortedTransitions();
    const query = transitionSearch.trim().toLowerCase();
    const searched = !query
      ? transitions
      : transitions.filter((transition) => {
        const snapshot =
          transition.linked_device_snapshot ||
          transition.validation_context?.linked_device_snapshot;
        const children = Array.isArray(snapshot?.children)
          ? snapshot.children
          : [];
        const coordSource = children.find(
          (child) =>
            Number.isFinite(Number(child?.lat)) &&
            Number.isFinite(Number(child?.lng)),
        );
        const lat = coordSource ? Number(coordSource.lat) : null;
        const lng = coordSource ? Number(coordSource.lng) : null;
        
        const transitionLocation =
          transition?.location?.formatted_address ||
          coordSource?.reverse_geocoded_address?.formatted_address ||
          (coordSource?.location && String(coordSource.location) !== "N/A"
            ? String(coordSource.location)
            : null) ||
          `${coordSource.position_latitude} | ${coordSource.position_longitude}`;
        const transitionCoords =
          lat != null &&
            lng != null &&
            Number.isFinite(lat) &&
            Number.isFinite(lng)
            ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
            : "";
        const stageRef = stagesByTransitionId.get(transition.id);
        const stageDelta = stageRef ? Number(stageRef.stage.delta) : null;

        const haystack = [
          transition.id,
          transition.from_state,
          transition.to_state,
          transition.trigger_reason,
          transition.trigger_channel,
          transition.trigger_value,
          transition.transition_timestamp,
          transitionLocation,
          transitionCoords,
          stageDelta,
        ]
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");

        return haystack.includes(query);
      });

    if (!showDeltaOnly) return searched;

    return searched.filter((transition) => {
      const fromStateUpper = String(transition.from_state || "").toUpperCase();
      const toStateUpper = String(transition.to_state || "").toUpperCase();
      const isTargetTransition =
        (fromStateUpper === "FINISHING" && toStateUpper === "LOADED") ||
        (fromStateUpper === "LOADED" && toStateUpper === "DISCHARGE");
      if (!isTargetTransition) return false;

      const stageRef = stagesByTransitionId.get(transition.id);
      const stageDelta = stageRef ? Number(stageRef.stage.delta) : null;
      return stageDelta != null && Number.isFinite(stageDelta);
    });
  };

  const filteredTransitions = useMemo(
    () => getFilteredTransitions(),
    [
      job?.transition_logs,
      sortColumn,
      sortDirection,
      showValidOnly,
      transitionSearch,
      showDeltaOnly,
      stagesByTransitionId,
    ],
  );

  const getGallonSummaries = useMemo(() => {
    const summary = {
      transitionRows: [],
      locationRows: [],
      totalTransitionNet: 0,
      totalLocationNet: 0,
      totalLocationDurationSeconds: 0,
    };

    if (!job?.transition_logs) return summary;

    const transGroups = new Map();
    const locGroups = new Map();

    const durationById = getDurationById(filteredTransitions);
    filteredTransitions.forEach((t) => {
      // Transition Totals
      const label = `${t.from_state || "??"} → ${t.to_state || "??"}`;
      const stageRef = stagesByTransitionId.get(t.id);
      const delta = stageRef ? Number(stageRef.stage.delta || 0) : 0;

      const tPrev = transGroups.get(label) || { label, net: 0, count: 0 };
      transGroups.set(label, {
        label,
        net: tPrev.net + delta,
        count: tPrev.count + 1,
      });
      summary.totalTransitionNet += delta;

      // Location Totals
      const locLabel = resolveTransitionLocationLabel(t);
      const snapshot =
        t.linked_device_snapshot ||
        t.validation_context?.linked_device_snapshot;
      const children = Array.isArray(snapshot?.children)
        ? snapshot.children
        : [];
      const coordSource = children.find(
        (c) =>
          Number.isFinite(Number(c?.lat)) && Number.isFinite(Number(c?.lng)),
      );
      const coords = coordSource
        ? `${Number(coordSource.lat).toFixed(4)}, ${Number(
          coordSource.lng,
        ).toFixed(4)}`
        : "N/A";

      const dur = durationById.get(t.id) || 0;
      const lPrev = locGroups.get(locLabel) || {
        label: locLabel,
        coords,
        net: 0,
        durationSeconds: 0,
        count: 0,
      };
      locGroups.set(locLabel, {
        ...lPrev,
        net: lPrev.net + delta,
        durationSeconds: lPrev.durationSeconds + dur,
        count: lPrev.count + 1,
      });
      summary.totalLocationNet += delta;
      summary.totalLocationDurationSeconds += dur;
    });

    summary.transitionRows = Array.from(transGroups.values()).filter(
      (r) => Math.abs(r.net) > 0.0001,
    );
    summary.locationRows = Array.from(locGroups.values());
    return summary;
  }, [job, filteredTransitions, stagesByTransitionId]);

  const TransitionsTable = () => {
    const summary = getGallonSummaries;
    const transitionRowsWithGallons = summary.transitionRows;
    const renderNet = (v) => `${v > 0 ? "+" : ""}${v.toFixed(2)} gal`;

    return (
      <MDBox sx={{}}>
        {/* --- Summary Section --- */}
        <MDBox p={2}>
          <Grid container spacing={2}>
            {/* Totals by Transition Type */}
            <Grid item xs={12} xl={6}>
              <Card
                sx={{
                  border: "1px solid gray",
                  background: "#191f34",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1}
                  px={2}
                  bgColor="#191f34"
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    setShowTransitionTypeTotals(!showTransitionTypeTotals)
                  }
                >
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    textTransform="uppercase"
                  >
                    Totals by Transition Type
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color={
                      summary.totalTransitionNet >= 0 ? "success" : "warning"
                    }
                  >
                    {renderNet(summary.totalTransitionNet)}
                  </MDTypography>
                </MDBox>
                <Collapse in={showTransitionTypeTotals}>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small">
                      <TableHead
                        sx={{
                          display: "table-header-group",
                          bgcolor: "grey-50",
                        }}
                      >
                        <TableRow>
                          <TableCell sx={{ fontSize: "10px", py: 1 }}>
                            Transition
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: "10px", py: 1 }}
                          >
                            Net Δ
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: "10px", py: 1 }}
                          >
                            Count
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transitionRowsWithGallons.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              align="center"
                              sx={{ fontSize: "11px", py: 2 }}
                            >
                              No gallon changes.
                            </TableCell>
                          </TableRow>
                        ) : (
                          transitionRowsWithGallons.map((row) => (
                            <TableRow key={row.label}>
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  py: 0.5,
                                  fontFamily: "monospace",
                                }}
                              >
                                {row.label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: "11px",
                                  py: 0.5,
                                  fontWeight: "bold",
                                }}
                              >
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color={row.net >= 0 ? "success" : "warning"}
                                >
                                  {renderNet(row.net)}
                                </MDTypography>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontSize: "11px",
                                  py: 0.5,
                                  color: "text",
                                }}
                              >
                                {row.count}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            </Grid>

            {/* Totals by Location */}
            <Grid item xs={12} xl={6}>
              <Card
                sx={{
                  border: "1px solid gray",
                  background: "#191f34",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1}
                  px={2}
                  bgColor="#191f34"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowLocationTotals(!showLocationTotals)}
                >
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    textTransform="uppercase"
                  >
                    Totals by Location
                  </MDTypography>
                  <MDBox textAlign="right">
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      display="block"
                      color={
                        summary.totalLocationNet >= 0 ? "success" : "warning"
                      }
                    >
                      {renderNet(summary.totalLocationNet)}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      sx={{ fontSize: "10px", color: "#fff" }}
                    >
                      {formatDuration(summary.totalLocationDurationSeconds)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <Collapse in={showLocationTotals}>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small">
                      <TableHead
                        sx={{
                          display: "table-header-group",
                          bgcolor: "grey-50",
                        }}
                      >
                        <TableRow>
                          <TableCell sx={{ fontSize: "10px", py: 1 }}>
                            Location
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: "10px", py: 1 }}
                          >
                            Net Δ
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: "10px", py: 1 }}
                          >
                            Time
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.locationRows.map((row) => (
                          <TableRow key={row.label}>
                            <TableCell sx={{ py: 0.5 }}>
                              <MDTypography
                                variant="caption"
                                display="block"
                                sx={{
                                  fontWeight: "medium",
                                  fontSize: "10px",
                                  maxWidth: 200,
                                }}
                                noWrap
                              >
                                {row.label}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ fontSize: "9px" }}
                                noWrap
                              >
                                {row.coords}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color={row.net >= 0 ? "success" : "warning"}
                              >
                                {renderNet(row.net)}
                              </MDTypography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5 }}>
                              <MDTypography
                                variant="caption"
                                sx={{ fontSize: "10px", color: "#fff" }}
                              >
                                {formatDuration(row.durationSeconds)}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* --- Controls Section --- */}
        <MDBox
          p={2}
          pt={0}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showValidOnly}
                  onChange={(e) => setShowValidOnly(e.target.checked)}
                />
              }
              label={
                <MDTypography variant="caption" sx={{ fontSize: "0.8rem" }}>
                  Valid only
                </MDTypography>
              }
            />
            <MDButton
              size="small"
              variant={showDeltaOnly ? "contained" : "outlined"}
              // color={showDeltaOnly ? "warning" : "dark"}
              onClick={() => setShowDeltaOnly(!showDeltaOnly)}
              sx={{ height: 32, color: "#fff" }}
            >
              Delta only
            </MDButton>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={toggleAllTransitionsExpanded}
              sx={{ height: 32 }}
            >
              {filteredTransitions.every((t) => expandedTransitionIds.has(t.id))
                ? "Collapse all"
                : "Expand all"}
            </MDButton>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              placeholder="Search transitions..."
              value={transitionSearch}
              onChange={(e) => setTransitionSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: "#fff" }}>
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
                endAdornment: transitionSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setTransitionSearch("")}
                    >
                      <Icon fontSize="small">close</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <IconButton onClick={(e) => setShowColumnsModal(true)}>
              <Icon>view_column</Icon>
            </IconButton>
          </MDBox>
        </MDBox>

        {/* --- Table Section --- */}
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                {visibleColumns.id && (
                  <TableCell
                    onClick={() => handleSort("id")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    #{" "}
                    {sortColumn === "id" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.start && (
                  <TableCell
                    onClick={() => handleSort("start")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Start{" "}
                    {sortColumn === "start" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.stop && (
                  <TableCell
                    onClick={() => handleSort("stop")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Stop{" "}
                    {sortColumn === "stop" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.stageStart && (
                  <TableCell
                    onClick={() => handleSort("stageStart")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Stage Start{" "}
                    {sortColumn === "stageStart" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.stageEnd && (
                  <TableCell
                    onClick={() => handleSort("stageEnd")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Stage End{" "}
                    {sortColumn === "stageEnd" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.delta && (
                  <TableCell
                    align="right"
                    onClick={() => handleSort("delta")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Δ gal{" "}
                    {sortColumn === "delta" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.reason && (
                  <TableCell
                    align="left"
                    onClick={() => handleSort("reason")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Reason{" "}
                    {sortColumn === "reason" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.location && (
                  <TableCell
                    onClick={() => handleSort("location")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Location{" "}
                    {sortColumn === "location" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.duration && (
                  <TableCell
                    onClick={() => handleSort("duration")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Dur{" "}
                    {sortColumn === "duration" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.confidence && (
                  <TableCell
                    align="center"
                    onClick={() => handleSort("confidence")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Conf{" "}
                    {sortColumn === "confidence" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.time && (
                  <TableCell
                    align="right"
                    onClick={() => handleSort("timestamp")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Time{" "}
                    {sortColumn === "timestamp" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                {visibleColumns.timeAgo && (
                  <TableCell
                    align="right"
                    onClick={() => handleSort("timeAgo")}
                    sx={{
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    Time Ago{" "}
                    {sortColumn === "timeAgo" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </TableCell>
                )}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransitions.map((t) => {
                const isExpanded = expandedTransitionIds.has(t.id);
                const stageRef = stagesByTransitionId.get(t.id);
                const stageDelta = stageRef
                  ? Number(stageRef.stage.delta)
                  : null;
                const durationById = getDurationById(filteredTransitions);
                const locLabel = resolveTransitionLocationLabel(t);
                const confidence = t.validation_context?.validation?.confidence;

                return (
                  <React.Fragment key={t.id}>
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        bgcolor: isExpanded ? "grey-100" : "inherit",
                      }}
                      onClick={() => toggleTransitionExpanded(t.id)}
                    >
                      {visibleColumns.id && (
                        <TableCell sx={{ fontSize: "10px", py: 1.5 }}>
                          #{t.id}
                        </TableCell>
                      )}
                      {visibleColumns.start && (
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={t.from_state}
                            size="small"
                            variant="outlined"
                            sx={{
                              bgcolor: STATE_HEX[t.from_state] || "grey-500",
                              color: "white",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.stop && (
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={t.to_state}
                            size="small"
                            sx={{
                              bgcolor: STATE_HEX[t.to_state] || "grey-500",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.stageStart &&
                        (() => {
                          const ref = stagesByTransitionId.get(t.id);
                          const startValue =
                            ref?.stage.start_value != null
                              ? Number(ref.stage.start_value)
                              : null;
                          const endValue =
                            ref?.stage.end_value != null
                              ? Number(ref.stage.end_value)
                              : null;
                          const startRounded =
                            startValue != null ? Math.round(startValue) : null;
                          const endRounded =
                            endValue != null ? Math.round(endValue) : null;
                          const unchanged =
                            startRounded != null &&
                            endRounded != null &&
                            startRounded === endRounded;
                          return (
                            <TableCell sx={{ py: 1.5 }}>
                              <MDTypography
                                variant="caption"
                                sx={{ fontSize: "10px" }}
                              >
                                {unchanged
                                  ? ""
                                  : startRounded != null
                                    ? startRounded
                                    : "—"}
                              </MDTypography>
                            </TableCell>
                          );
                        })()}
                      {visibleColumns.stageEnd &&
                        (() => {
                          const ref = stagesByTransitionId.get(t.id);
                          const startValue =
                            ref?.stage.start_value != null
                              ? Number(ref.stage.start_value)
                              : null;
                          const endValue =
                            ref?.stage.end_value != null
                              ? Number(ref.stage.end_value)
                              : null;
                          const startRounded =
                            startValue != null ? Math.round(startValue) : null;
                          const endRounded =
                            endValue != null ? Math.round(endValue) : null;
                          const unchanged =
                            startRounded != null &&
                            endRounded != null &&
                            startRounded === endRounded;
                          return (
                            <TableCell sx={{ py: 1.5 }}>
                              <MDTypography
                                variant="caption"
                                sx={{ fontSize: "10px" }}
                              >
                                {unchanged
                                  ? ""
                                  : endRounded != null
                                    ? endRounded
                                    : "—"}
                              </MDTypography>
                            </TableCell>
                          );
                        })()}
                      {visibleColumns.delta && (
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          {stageDelta != null ? (
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={stageDelta >= 0 ? "success" : "warning"}
                            >
                              {stageDelta > 0 ? "+" : ""}
                              {stageDelta.toFixed(1)}
                            </MDTypography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.reason && (
                        <TableCell sx={{ py: 1.5 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ fontSize: "10px" }}
                            display="block"
                            title={t.trigger_reason ?? ""}
                          >
                            {t.trigger_reason ?? ""}
                          </MDTypography>
                        </TableCell>
                      )}
                      {visibleColumns.location && (
                        <TableCell sx={{ py: 1.5 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ fontSize: "10px", maxWidth: 200 }}
                            display="block"
                            noWrap
                          >
                            {locLabel}
                          </MDTypography>
                        </TableCell>
                      )}
                      {visibleColumns.duration && (
                        <TableCell sx={{ py: 1.5 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ fontSize: "10px" }}
                          >
                            {formatDuration(durationById.get(t.id))}
                          </MDTypography>
                        </TableCell>
                      )}
                      {visibleColumns.confidence && (
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {confidence != null && (
                            <MDBox
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor:
                                  confidence >= 0.95
                                    ? "success.main"
                                    : "warning.main",
                                mx: "auto",
                              }}
                              title={`${(confidence * 100).toFixed(
                                0,
                              )}% confidence`}
                            />
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.time && (
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ fontSize: "10px" }}
                          >
                            {new Date(
                              t.transition_timestamp,
                            ).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            })}
                          </MDTypography>
                        </TableCell>
                      )}
                      {visibleColumns.timeAgo && (
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <MDTypography
                            variant="caption"
                            sx={{ fontSize: "10px" }}
                          >
                            {formatTimeAgo(t.transition_timestamp)}
                          </MDTypography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Icon fontSize="small">
                          {isExpanded ? "expand_less" : "expand_more"}
                        </Icon>
                      </TableCell>
                    </TableRow>

                    {/* Expansion Row */}
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        sx={{ p: 0, borderBottom: "none" }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <MDBox p={3} bgColor="grey-50">
                            <Grid container spacing={3}>
                              {/* Stage Telemetry */}
                              {stageRef && (
                                <Grid item xs={12} md={6}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    display="block"
                                    mb={1}
                                  >
                                    Stage Info
                                  </MDTypography>
                                  <MDBox
                                    p={2}
                                    bgColor="#191f34"
                                    borderRadius="lg"
                                    border="1px solid"
                                    borderColor="grey-200"
                                  >
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      mb={1}
                                    >
                                      <Chip
                                        label={`${stageRef.stage.state_name} #${stageRef.stage.segment_index}`}
                                        size="small"
                                        sx={{
                                          bgcolor:
                                            STATE_HEX[
                                            stageRef.stage.state_name
                                            ],
                                          color: "white",
                                        }}
                                      />
                                      <Chip
                                        label={stageRef.role.toUpperCase()}
                                        size="small"
                                        variant="outlined"
                                        color={
                                          stageRef.role === "start"
                                            ? "info"
                                            : "warning"
                                        }
                                      />
                                    </MDBox>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                        >
                                          Start Vol
                                        </MDTypography>
                                        <MDTypography
                                          variant="button"
                                          display="block"
                                        >
                                          {Number(
                                            stageRef.stage.start_value,
                                          ).toFixed(1)}{" "}
                                          gal
                                        </MDTypography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                        >
                                          End Vol
                                        </MDTypography>
                                        <MDTypography
                                          variant="button"
                                          display="block"
                                        >
                                          {stageRef.stage.end_value != null
                                            ? `${Number(
                                              stageRef.stage.end_value,
                                            ).toFixed(1)} gal`
                                            : "—"}
                                        </MDTypography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                        >
                                          Cycle Type
                                        </MDTypography>
                                        <MDTypography
                                          variant="button"
                                          display="block"
                                          textTransform="capitalize"
                                        >
                                          {stageRef.cycle_type}
                                        </MDTypography>
                                      </Grid>
                                      <Grid item xs={6}>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                        >
                                          Duration
                                        </MDTypography>
                                        <MDTypography
                                          variant="button"
                                          display="block"
                                        >
                                          {formatDuration(
                                            (new Date(
                                              stageRef.stage.ended_at,
                                            ).getTime() -
                                              new Date(
                                                stageRef.stage.started_at,
                                              ).getTime()) /
                                            1000,
                                          )}
                                        </MDTypography>
                                      </Grid>
                                    </Grid>
                                    {stageRef.stage.end_reason && (
                                      <MDBox mt={1}>
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          fontStyle="italic"
                                        >
                                          End Reason:{" "}
                                          {stageRef.stage.end_reason}
                                        </MDTypography>
                                      </MDBox>
                                    )}
                                  </MDBox>
                                </Grid>
                              )}

                              {/* Validation Failures */}
                              <Grid item xs={12} md={6}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                  display="block"
                                  mb={1}
                                >
                                  Validation Status
                                </MDTypography>
                                <MDBox
                                  p={2}
                                  bgColor="#191f34"
                                  borderRadius="lg"
                                  border="1px solid"
                                  borderColor="grey-200"
                                >
                                  {t.validation_context?.validation?.failures
                                    ?.length > 0 ? (
                                    <MDBox>
                                      <MDTypography
                                        variant="caption"
                                        color="error"
                                        fontWeight="bold"
                                      >
                                        Failures (
                                        {
                                          t.validation_context.validation
                                            .failures.length
                                        }
                                        )
                                      </MDTypography>
                                      {t.validation_context.validation.failures.map(
                                        (f, i) => (
                                          <MDBox
                                            key={i}
                                            mt={1}
                                            p={1}
                                            bgColor="error"
                                            variant="gradient"
                                            borderRadius="md"
                                            opacity={0.8}
                                          >
                                            <MDTypography
                                              variant="caption"
                                              color="white"
                                              display="block"
                                              fontWeight="bold"
                                            >
                                              {f.rule_id}
                                            </MDTypography>
                                            <MDTypography
                                              variant="caption"
                                              color="white"
                                              display="block"
                                            >
                                              {f.error_message}
                                            </MDTypography>
                                          </MDBox>
                                        ),
                                      )}
                                    </MDBox>
                                  ) : (
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <Icon color="success">check_circle</Icon>
                                      <MDTypography
                                        variant="button"
                                        color="success"
                                      >
                                        Passed All Rules
                                      </MDTypography>
                                    </MDBox>
                                  )}
                                </MDBox>
                              </Grid>

                              {/* Debug Trace */}
                              {t.validation_context?.validation?.debug?.length >
                                0 && (
                                  <Grid item xs={12}>
                                    <MDTypography
                                      variant="button"
                                      fontWeight="bold"
                                      display="block"
                                      mb={1}
                                    >
                                      Debug Trace
                                    </MDTypography>
                                    <MDBox
                                      p={2}
                                      bgColor="#191f34"
                                      borderRadius="lg"
                                      border="1px solid"
                                      borderColor="grey-200"
                                      sx={{ maxHeight: 200, overflowY: "auto" }}
                                    >
                                      {t.validation_context.validation.debug.map(
                                        (log, i) => (
                                          <MDBox
                                            key={i}
                                            display="flex"
                                            alignItems="flex-start"
                                            gap={1}
                                            mb={0.5}
                                          >
                                            <Icon
                                              sx={{
                                                fontSize: "14px !important",
                                                color: log.passed
                                                  ? "success.main"
                                                  : "error.main",
                                                mt: 0.5,
                                              }}
                                            >
                                              {log.passed
                                                ? "check_circle"
                                                : "cancel"}
                                            </Icon>
                                            <MDTypography
                                              variant="caption"
                                              sx={{
                                                fontFamily: "monospace",
                                                fontSize: "10px",
                                                mt: 1,
                                              }}
                                            >
                                              {log.message || JSON.stringify(log)}
                                            </MDTypography>
                                          </MDBox>
                                        ),
                                      )}
                                    </MDBox>
                                  </Grid>
                                )}

                              {/* Channels */}
                              {t.validation_context?.channels &&
                                Object.keys(t.validation_context.channels)
                                  .length > 0 && (
                                  <Grid item xs={12}>
                                    <MDTypography
                                      variant="button"
                                      fontWeight="bold"
                                      display="block"
                                      mb={1}
                                    >
                                      Channel Diagnostics
                                    </MDTypography>
                                    <Grid container spacing={2}>
                                      {Object.entries(
                                        t.validation_context.channels,
                                      ).map(([id, ch]) => (
                                        <Grid
                                          item
                                          xs={12}
                                          md={4}
                                          xl={3}
                                          key={id}
                                        >
                                          <MDBox
                                            p={1.5}
                                            bgColor="#191f34"
                                            borderRadius="lg"
                                            border="1px solid"
                                            borderColor="info.main"
                                            sx={{ borderLeftWidth: 4 }}
                                          >
                                            <MDBox
                                              display="flex"
                                              justifyContent="space-between"
                                              alignItems="center"
                                              mb={0.5}
                                            >
                                              <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                color="info"
                                              >
                                                Ch #{id}
                                              </MDTypography>
                                              {ch.state?.display_label && (
                                                <Chip
                                                  label={ch.state.display_label}
                                                  size="small"
                                                  sx={{
                                                    height: 16,
                                                    fontSize: "9px",
                                                    bgcolor:
                                                      ch.state.display_color ||
                                                      "grey-400",
                                                    color: "white",
                                                  }}
                                                />
                                              )}
                                            </MDBox>
                                            <MDTypography
                                              variant="caption"
                                              color="text"
                                              display="block"
                                            >
                                              Name: {ch.channel_name || "N/A"}
                                            </MDTypography>
                                            <MDBox
                                              display="flex"
                                              justifyContent="space-between"
                                              mt={0.5}
                                            >
                                              <MDBox>
                                                <MDTypography
                                                  variant="caption"
                                                  color="text"
                                                  sx={{ fontSize: "9px" }}
                                                >
                                                  Value
                                                </MDTypography>
                                                <MDTypography
                                                  variant="button"
                                                  display="block"
                                                  sx={{ fontSize: "11px" }}
                                                >
                                                  {Number.isFinite(
                                                    Number(ch.current_value),
                                                  )
                                                    ? Number(
                                                      ch.current_value,
                                                    ).toFixed(1)
                                                    : ch.current_value}
                                                </MDTypography>
                                              </MDBox>
                                              <MDBox>
                                                <MDTypography
                                                  variant="caption"
                                                  color="text"
                                                  sx={{ fontSize: "9px" }}
                                                >
                                                  Volume
                                                </MDTypography>
                                                <MDTypography
                                                  variant="button"
                                                  display="block"
                                                  sx={{ fontSize: "11px" }}
                                                >
                                                  {ch.volume_level != null
                                                    ? `${Number(
                                                      ch.volume_level,
                                                    ).toFixed(1)} gal`
                                                    : "—"}
                                                </MDTypography>
                                              </MDBox>
                                            </MDBox>
                                          </MDBox>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  </Grid>
                                )}
                            </Grid>
                          </MDBox>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Column Config Dialog */}
        <Dialog
          open={showColumnsModal}
          onClose={() => setShowColumnsModal(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogContent dividers>
            <MDBox display="flex" flexDirection="column">
              {Object.keys(visibleColumns).map((col) => (
                <FormControlLabel
                  key={col}
                  control={
                    <Checkbox
                      checked={visibleColumns[col]}
                      onChange={() => toggleVisibleColumn(col)}
                    />
                  }
                  label={
                    <MDTypography variant="button" textTransform="capitalize">
                      {col}
                    </MDTypography>
                  }
                />
              ))}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="info" onClick={() => setShowColumnsModal(false)}>
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    );
  };

  const mapPoints = useMemo(() => {
    if (!job?.transition_logs) return [];
    // Always show only valid transitions on the map
    const baseTransitions = job.transition_logs.filter(
      (transition) =>
        transition.validation_context?.validation?.allowed === true,
    );

    const chronological = [...baseTransitions].sort(
      (a, b) =>
        new Date(a.transition_timestamp).getTime() -
        new Date(b.transition_timestamp).getTime(),
    );

    const points = [];

    chronological.forEach((transition) => {
      const snapshot =
        transition.linked_device_snapshot ||
        transition.validation_context?.linked_device_snapshot;
      const children = Array.isArray(snapshot?.children)
        ? snapshot.children
        : [];
      const coordSource = children.find(
        (child) =>
          Number.isFinite(Number(child?.lat)) &&
          Number.isFinite(Number(child?.lng)),
      );

      if (!coordSource) return;

      const lat = Number(coordSource.lat);
      const lng = Number(coordSource.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const resolvedLocationName =
        transition?.location?.formatted_address ||
        coordSource?.reverse_geocoded_address?.formatted_address ||
        (coordSource?.address && String(coordSource.address) !== "N/A"
          ? String(coordSource.address)
          : null) ||
        (coordSource?.location && String(coordSource.location) !== "N/A"
          ? String(coordSource.location)
          : null) ||
        undefined;
      points.push({
        lat,
        lng,
        transitionId: transition.id,
        timestamp: transition.transition_timestamp,
        state: transition.to_state ?? transition.from_state ?? undefined,
        locationName: resolvedLocationName,
      });
    });

    return points;
  }, [job]);

  const truckLocation = useMemo(() => {
    if (!job?.transition_logs || job.transition_logs.length === 0) return null;

    // Get the last transition in chronological order
    const chronological = [...job.transition_logs].sort(
      (a, b) =>
        new Date(a.transition_timestamp).getTime() -
        new Date(b.transition_timestamp).getTime(),
    );
    const lastTransition = chronological[chronological.length - 1];

    const snapshot =
      lastTransition.linked_device_snapshot ||
      lastTransition.validation_context?.linked_device_snapshot;
    const children = Array.isArray(snapshot?.children) ? snapshot.children : [];
    const firstChild = children[0];

    if (
      firstChild &&
      Number.isFinite(Number(firstChild.position_latitude)) &&
      Number.isFinite(Number(firstChild.position_longitude))
    ) {
      return {
        lat: Number(firstChild.position_latitude),
        lng: Number(firstChild.position_longitude),
      };
    }
    return null;
  }, [job]);

  if (loading)
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress color="success" />
      </MDBox>
    );

  if (error || !job)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <MDTypography variant="h6" color="error">
            Error: {error || "Job not found"}
          </MDTypography>
          <MDButton
            onClick={() => navigate(-1)}
            variant="gradient"
            color="info"
            sx={{ mt: 2 }}
          >
            Go Back
          </MDButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );

  const totalTransitions = job.transition_logs?.length ?? 0;

  const truckAsset = job?.truck;

  const intakeVolume = getVolumeValue(
    job.volume_collected_gallons ??
    job.final_metrics?.volumes?.collected ??
    job.final_metrics?.volumes?.collected_gallons,
  );

  const dischargeVolume = getVolumeValue(
    job.volume_discharged_gallons ??
    job.final_metrics?.volumes?.discharged ??
    job.final_metrics?.volumes?.discharged_gallons,
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              onClick={handleAnalyzeWithAi}
            >
              <Icon sx={{ mr: 1 }}>smart_toy</Icon> AI Insight
            </MDButton>
            <MDButton
              variant="outlined"
              sx={{ color: '#fff' }}
              size="small"
              onClick={handlePreviewPdf}
              disabled={isPreviewingPDF}
            >
              <Icon sx={{ mr: 1 }}>visibility</Icon> {isPreviewingPDF ? 'Previewing…' : 'Preview PDF'}
            </MDButton>
            <MDButton
              variant="outlined"
              sx={{ color: '#fff' }}
              size="small"
              onClick={handleExportPdf}
              disabled={isExportingPDF}
            >
              <Icon sx={{ mr: 1 }}>download</Icon> {isExportingPDF ? 'Exporting…' : 'Export PDF'}
            </MDButton>
            <MDButton
              variant="outlined"
              sx={{ color: '#fff' }}
              size="small"
              onClick={exportTransitionsToCSV}
            >
              <Icon sx={{ mr: 1 }}>download</Icon> Export Excel
            </MDButton>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDTypography variant="h4" fontWeight="bold">
                    {job.job_session_name || `Job #${job.id}`}
                  </MDTypography>
                  <Chip
                    label={job.current_state}
                    sx={{
                      bgcolor: STATE_HEX[job.current_state],
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </MDBox>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ mt: 1, display: "block", fontSize: "0.85rem" }}
                >
                  Config ID: {job.operation_config_id || "N/A"} | Device ID:{" "}
                  {job.device_id || "N/A"} | Asset ID:{" "}
                  {job.entity_address_item_id || "N/A"}
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          {/* Stats */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  <Icon
                    sx={{ verticalAlign: "middle", mr: 1, color: "info.main" }}
                  >
                    schedule
                  </Icon>{" "}
                  Timeline
                </MDTypography>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  mt={2}
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Started
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {job.started_at ? formatDate(job.started_at) : "N/A"}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Completed
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {job.completed_at ? formatDate(job.completed_at) : "-"}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Duration
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {formatDuration(job.total_duration_seconds)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  <Icon
                    sx={{
                      verticalAlign: "middle",
                      mr: 1,
                      color: "success.main",
                    }}
                  >
                    water_drop
                  </Icon>{" "}
                  Volumes (gal)
                </MDTypography>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  mt={2}
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Intake
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {formatVolumeDisplay(intakeVolume)}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Discharge
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {formatVolumeDisplay(dischargeVolume)}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      Residual
                    </MDTypography>
                    <MDTypography variant="button" display="block">
                      {job.residual_volume_gallons != null
                        ? formatVolumeDisplay(
                          Number(job.residual_volume_gallons),
                        )
                        : "—"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Truck / Asset Card */}
          {truckAsset && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    <Icon
                      sx={{
                        verticalAlign: "middle",
                        mr: 1,
                        color: "warning.main",
                      }}
                    >
                      local_shipping
                    </Icon>{" "}
                    Asset Details
                  </MDTypography>
                  <MDBox
                    display="flex"
                    justifyContent="flex-start"
                    mt={2}
                    flexWrap="wrap"
                    gap={6}
                  >
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Truck Name
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {truckAsset.name || "N/A"}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Map ID
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {truckAsset.map_identifier}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Item/Asset ID
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {truckAsset.address_item_id}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Entity
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {truckAsset.entity_id}
                      </MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        Addr
                      </MDTypography>
                      <MDTypography variant="button" display="block">
                        {truckAsset.address_id}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Map Section */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ cursor: "pointer" }}
                onClick={() => setShowRouteMap(!showRouteMap)}
              >
                <MDTypography variant="h6" display="flex" alignItems="center">
                  <Icon sx={{ mr: 1 }}>map</Icon> Route Visualization
                </MDTypography>
                <Icon>{showRouteMap ? "expand_less" : "expand_more"}</Icon>
              </MDBox>
              <Collapse in={showRouteMap}>
                <MDBox sx={{ height: 400, position: "relative" }}>
                  <Suspense
                    fallback={
                      <MDBox p={3} textAlign="center">
                        <CircularProgress size={20} />
                      </MDBox>
                    }
                  >
                    <JobRouteMapGoogle
                      points={mapPoints}
                      trackPoints={routeTrackPoints}
                      truckLocation={truckLocation}
                      onToggleFullscreen={() => setFullscreenMap(true)}
                    />
                  </Suspense>
                </MDBox>
              </Collapse>
            </Card>
          </Grid>

          {/* Fullscreen Map Dialog */}
          <Dialog
            fullScreen
            open={fullscreenMap}
            onClose={() => setFullscreenMap(false)}
          >
            <MDBox display="flex" flexDirection="column" height="100vh">
              <MDBox
                p={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor="info"
                variant="gradient"
              >
                <MDTypography variant="h6" color="white">
                  Route Visualization - Fullscreen
                </MDTypography>
                <IconButton
                  onClick={() => setFullscreenMap(false)}
                  color="inherit"
                >
                  <Icon color="white">close</Icon>
                </IconButton>
              </MDBox>
              <MDBox flex={1} position="relative">
                <Suspense
                  fallback={
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <CircularProgress />
                    </MDBox>
                  }
                >
                  <JobRouteMapGoogle
                    points={mapPoints}
                    trackPoints={routeTrackPoints}
                    truckLocation={truckLocation}
                    fullscreen
                    onToggleFullscreen={() => setFullscreenMap(false)}
                  />
                </Suspense>
              </MDBox>
            </MDBox>
          </Dialog>

          {/* Table Section */}
          {totalTransitions > 0 && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: "#191f34", border: "1px solid grey" }}>
                <MDBox
                  p={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() =>
                    setShowTransitionsSection(!showTransitionsSection)
                  }
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon
                      sx={{
                        fontSize: "14px !important",
                        transition: "transform 0.2s",
                        transform: showTransitionsSection
                          ? "rotate(0deg)"
                          : "rotate(-90deg)",
                        color: "#fff",
                      }}
                    >
                      expand_more
                    </Icon>
                    <Icon sx={{ color: "success.main" }}>history</Icon>
                    <MDTypography
                      variant="h6"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                    >
                      State Transitions
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        ({filteredTransitions.length})
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="text"
                      color="info"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportTransitionsToCSV();
                      }}
                    >
                      <Icon sx={{ mr: 1 }}>download</Icon> Export CSV
                    </MDButton>
                  </MDBox>
                </MDBox>
                <Collapse in={showTransitionsSection}>
                  <MDBox pb={2}>
                    {TransitionsTable()}
                  </MDBox>
                </Collapse>
              </Card>
            </Grid>
          )}

          {/* Metadata Footer */}
          <Grid item xs={12}>
            <MDBox
              display="flex"
              justifyContent="center"
              p={2}
              opacity={0.6}
              gap={4}
              flexWrap="wrap"
            >
              <MDTypography sx={{ fontSize: "0.85rem", color: "#fff" }}>
                Metric Source:{" "}
                {job.metric_source || job.final_metrics?.metric_source || "N/A"}
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ fontSize: "0.85rem", color: "#fff" }}
              >
                Last Updated: {formatDate(job.updated_at)}
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ fontSize: "0.85rem", color: "#fff" }}
              >
                Auto Created: {job.is_auto_created ? "Yes" : "No"}
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ fontSize: "0.85rem", color: "#fff" }}
              >
                Trigger: {job.metadata.trigger_reason}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* AI Insight Dialog */}
      <Dialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MDTypography variant="h6">AI Transition Analysis</MDTypography>
          <Icon sx={{ cursor: "pointer" }} onClick={() => setAiOpen(false)}>
            close
          </Icon>
        </DialogTitle>
        <DialogContent dividers>
          {aiLoading && (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={3}
            >
              <CircularProgress color="success" size={24} sx={{ mb: 2 }} />
              <MDTypography variant="button">
                Analyzing telemetry data...
              </MDTypography>
            </MDBox>
          )}

          {aiError && (
            <MDBox p={1} bgColor="grey-100" borderRadius="lg">
              <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#000" }}>
                {aiError}
              </MDTypography>
            </MDBox>
          )}

          {!aiLoading && !aiError && (
            <MDBox p={1} bgColor="grey-100" borderRadius="lg">
              <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#000" }}>
                {aiAnswer}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            color="info"
            variant="text"
            onClick={() => setAiOpen(false)}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
